import type { GlobalToken } from 'antd/es/theme/interface';

import type { DocumentConcept } from './documentIntelligence';

export interface DocumentConceptPalette {
    activeBackground: string;
    background: string;
    border: string;
    domainBackground: string;
    text: string;
}

interface PaletteFamily {
    backgrounds: readonly [string, string, string];
    borders: readonly [string, string, string];
    text: readonly [string, string, string];
}

const hashValue = (value: string) => {
    let hash = 0;

    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
    }

    return hash;
};

const getPaletteFamilies = (token: GlobalToken): readonly PaletteFamily[] => [
    {
        backgrounds: [token.blue1, token.blue2, token.blue3],
        borders: [token.blue4, token.blue5, token.blue6],
        text: [token.blue7, token.blue8, token.blue9],
    },
    {
        backgrounds: [token.purple1, token.purple2, token.purple3],
        borders: [token.purple4, token.purple5, token.purple6],
        text: [token.purple7, token.purple8, token.purple9],
    },
    {
        backgrounds: [token.cyan1, token.cyan2, token.cyan3],
        borders: [token.cyan4, token.cyan5, token.cyan6],
        text: [token.cyan7, token.cyan8, token.cyan9],
    },
    {
        backgrounds: [token.green1, token.green2, token.green3],
        borders: [token.green4, token.green5, token.green6],
        text: [token.green7, token.green8, token.green9],
    },
    {
        backgrounds: [token.magenta1, token.magenta2, token.magenta3],
        borders: [token.magenta4, token.magenta5, token.magenta6],
        text: [token.magenta7, token.magenta8, token.magenta9],
    },
    {
        backgrounds: [token.volcano1, token.volcano2, token.volcano3],
        borders: [token.volcano4, token.volcano5, token.volcano6],
        text: [token.volcano7, token.volcano8, token.volcano9],
    },
    {
        backgrounds: [token.orange1, token.orange2, token.orange3],
        borders: [token.orange4, token.orange5, token.orange6],
        text: [token.orange7, token.orange8, token.orange9],
    },
    {
        backgrounds: [token.gold1, token.gold2, token.gold3],
        borders: [token.gold4, token.gold5, token.gold6],
        text: [token.gold7, token.gold8, token.gold9],
    },
    {
        backgrounds: [token.lime1, token.lime2, token.lime3],
        borders: [token.lime4, token.lime5, token.lime6],
        text: [token.lime7, token.lime8, token.lime9],
    },
    {
        backgrounds: [token.geekblue1, token.geekblue2, token.geekblue3],
        borders: [token.geekblue4, token.geekblue5, token.geekblue6],
        text: [token.geekblue7, token.geekblue8, token.geekblue9],
    },
    {
        backgrounds: [token.red1, token.red2, token.red3],
        borders: [token.red4, token.red5, token.red6],
        text: [token.red7, token.red8, token.red9],
    },
];

export const getDocumentConceptPalette = (
    token: GlobalToken,
    concept: Pick<DocumentConcept, 'classification' | 'id' | 'primaryDomainId'>,
    domainOrder?: number,
): DocumentConceptPalette => {
    const families = getPaletteFamilies(token);

    if (!concept.primaryDomainId) {
        const conceptHash = hashValue(concept.id);
        const family = families[conceptHash % families.length];
        const conceptVariant = Math.floor(conceptHash / families.length) % family.backgrounds.length;

        return {
            activeBackground: family.backgrounds[Math.min(conceptVariant + 1, family.backgrounds.length - 1)],
            background: family.backgrounds[conceptVariant],
            border: family.borders[conceptVariant],
            domainBackground: token.colorFillTertiary,
            text: family.text[conceptVariant],
        };
    }

    const familyIndex = domainOrder ? domainOrder - 1 : hashValue(concept.primaryDomainId);
    const family = families[familyIndex % families.length];
    const conceptVariant = hashValue(concept.id) % family.backgrounds.length;

    return {
        activeBackground: family.backgrounds[Math.min(conceptVariant + 1, family.backgrounds.length - 1)],
        background: family.backgrounds[conceptVariant],
        border: family.borders[conceptVariant],
        domainBackground: family.backgrounds[0],
        text: family.text[conceptVariant],
    };
};

export const getDocumentDomainPalette = (token: GlobalToken, concept: DocumentConcept, domainOrder?: number) => {
    const palette = getDocumentConceptPalette(token, concept, domainOrder);

    return { background: palette.domainBackground, border: palette.border, text: palette.text };
};
